<script lang="ts">
    import { faPenNib, faTrashAlt, faSave, faTimes } from '@fortawesome/free-solid-svg-icons'
    import type { AudienceRule } from '../../../modules/eventsApi/audienceRules/audience-rule'
    import type { AudienceRuleCreateDto } from '../../../modules/eventsApi/audienceRules/audience-rule-create.dto'
    import { AudienceRulesRepository } from '../../../modules/eventsApi/audienceRules/audience-rules.repository'
    import { AudienceRulesService } from '../../../modules/eventsApi/audienceRules/audience-rules.service'
    import { createEventDispatcher, onMount } from 'svelte'
    import type { AudienceRuleUpdateDto } from '../../../modules/eventsApi/audienceRules/audience-rule-update.dto'
    import { EventsRepository } from '../../../modules/eventsApi/events/events.repository'
    import { EventsService } from '../../../modules/eventsApi/events/events.service'
    import dayjs from 'dayjs'
    import utc from 'dayjs/plugin/utc'
    dayjs.extend(utc)
    import { AudienceRuleAct, AudienceRuleDateType } from '../../../modules/eventsApi/audienceRules/audiences.enum'
    import { PreviewOrGenerateAudienceDto } from '../../../modules/eventsApi/audiences/dto/audience-preview-generate.dto'
    import { AudiencesRepository } from '../../../modules/eventsApi/audiences/audiences.repository'
    import { AudiencesService } from '../../../modules/eventsApi/audiences/audiences.service'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import type { AutomationSourceEnum } from '../../../modules/automationApi/automations/automation-source.enum'
    const dispatch = createEventDispatcher()

    const notAllowedEvents = ['publico_', 'automacao_', 'agendamento_excluido', 'cartao_movimentado']

    export let rule: AudienceRule
    export let type: string
    export let firstRule: boolean
    export let source: AutomationSourceEnum = null

    let edit: boolean = false
    let show: boolean = false
    let ruleSelectedEvent: string[] = []
    let ruleSelectedValue: string[] = []
    let fixedDateFrom = new Date()
    let fixedDateTo = new Date()
    let dynamicDateFrom = 0
    let dynamicDateTo = 0
    let dynamicDateFactor = 0
    let bgRed: boolean = false
    let lineRed: boolean = false

    const ruleR: AudienceRulesRepository = new AudienceRulesRepository('audiences/' + rule.audienceId + '/rules')
    const ruleS: AudienceRulesService = new AudienceRulesService(ruleR)

    const audienceR: AudiencesRepository = new AudiencesRepository()
    const audienceS: AudiencesService = new AudiencesService(audienceR)

    const eventR: EventsRepository = new EventsRepository()
    const eventS: EventsService = new EventsService(eventR)

    async function startEdit() {
        await loadEventTypes()
        // regra padrão 1 (OR)
        edit = true
    }

    async function removeRule() {
        await ruleS.remove(rule.audienceRuleId)
        dispatch('change')
        edit = false
    }

    function cancelEdit() {
        dispatch('cancel')
        edit = false
    }

    function saveRule() {
        if (rule.audienceRuleId) {
            updateRule()
        } else {
            createRule()
        }
        bgRed = bgRed
        lineRed = lineRed
    }

    let eventTypes = []
    async function loadEventTypes() {
        const result: string[] = await eventS.getEventTypes()
        eventTypes = []
        for (const et of result) {
            if (notAllowedEvents.some((el) => et.startsWith(el))) continue
            eventTypes.push(et)
        }
    }

    let eventValues = []
    async function loadEventValues() {}

    async function updatePreview() {
        try {
            show = true
            let ruleDateFrom = 0
            let ruleDateTo = 0
            if (rule.dateType === AudienceRuleDateType.Fixed) {
                ruleDateFrom = +fixedDateFrom
                ruleDateTo = +fixedDateTo + 86400999 // adiciona 1 dia para incluir o dia todo
            }
            if (rule.dateType === AudienceRuleDateType.Relative) {
                ruleDateFrom = dynamicDateFrom * dynamicDateFactor
                ruleDateTo = dynamicDateTo * dynamicDateFactor //+ 86400999 // adiciona 1 dia para incluir o dia todo
            }
            rule.dateFrom = ruleDateFrom / 1000
            rule.dateTo = ruleDateTo / 1000
            rule.event = ruleSelectedEvent.join(',')
            if (!rule.value) rule.value = ''

            let data: PreviewOrGenerateAudienceDto = new PreviewOrGenerateAudienceDto()
            data.audienceId = rule.audienceId
            data.audienceRules = []
            data.audienceRules.push(rule)

            if (!validateRule(rule)) return
            let result = await audienceS.getPreview(data)
            rule.total = result.evaluatedRules[0].totalDistinct ?? null
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function createRule() {
        try {
            show = true
            let ruleDateFrom = 0
            let ruleDateTo = 0
            if (rule.dateType === AudienceRuleDateType.Fixed) {
                ruleDateFrom = +fixedDateFrom
                ruleDateTo = +fixedDateTo
                ruleDateTo = +fixedDateTo + 86400999 // adiciona 1 dia para incluir o dia todo
            }
            if (rule.dateType === AudienceRuleDateType.Relative) {
                ruleDateFrom = dynamicDateFrom * dynamicDateFactor
                ruleDateTo = dynamicDateTo * dynamicDateFactor
            }
            const dto: AudienceRuleCreateDto = {
                act: rule.act,
                dateReference: rule.dateReference,
                dateFrom: ruleDateFrom / 1000,
                dateTo: ruleDateTo / 1000,
                dateType: rule.dateType,
                event: ruleSelectedEvent.join(','),
                // value: ruleSelectedValue.join(','),
                value: rule.value || '',
                lastEvent: rule.lastEvent,
            }
            if (!validateRule(dto)) return
            let result = await ruleS.create(dto)
            dispatch('change', { detail: result })
            edit = false
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateRule() {
        try {
            show = true
            let ruleDateFrom = 0
            let ruleDateTo = 0
            if (rule.dateType === AudienceRuleDateType.Fixed) {
                ruleDateFrom = +fixedDateFrom
                ruleDateTo = +fixedDateTo
                ruleDateTo = +fixedDateTo + 86400999 // adiciona 1 dia para incluir o dia todo
            }
            if (rule.dateType === AudienceRuleDateType.Relative) {
                ruleDateFrom = dynamicDateFrom * dynamicDateFactor
                ruleDateTo = dynamicDateTo * dynamicDateFactor
            }
            const dto: AudienceRuleUpdateDto = {
                act: rule.act,
                dateReference: rule.dateReference,
                dateFrom: ruleDateFrom / 1000,
                dateTo: ruleDateTo / 1000,
                dateType: rule.dateType,
                event: ruleSelectedEvent.join(','),
                // value: ruleSelectedValue.join(','),
                value: rule.value || '',
                lastEvent: rule.lastEvent,
            }
            if (!validateRule(dto)) return
            let result = await ruleS.update(rule.audienceRuleId, dto)
            dispatch('change', { detail: result })
            edit = false
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateRule(dto) {
        if (isNaN(dto.dateFrom) || isNaN(dto.dateTo)) {
            evaluateError('Informe as datas corretamente')
            return false
        }
        if (dto.act === null) {
            evaluateError('Informe o tipo de regra (adicionar ao público, remover do público...)')
            return false
        }
        if (!dto.event) {
            evaluateError('Informe ao menos 1 evento')
            return false
        }
        if (!dto.dateType) {
            evaluateError('Informe a data em que o evento ocorre')
            return false
        }
        if (dto.dateFrom === null || dto.dateFrom === undefined || dto.dateTo === null || dto.dateTo === undefined) {
            evaluateError('Informe as datas corretamente')
            return false
        }
        if (dto.dateFrom > dto.dateTo) {
            if (dto.dateFrom <= 0 && dto.dateTo <= 0) {
                const temp = dto.dateFrom
                dto.dateFrom = dto.dateTo
                dto.dateTo = temp
            } else {
                evaluateError('A data inicial deve ser menor ou igual a data final')
                return false
            }
        }
        if (dto.dateFrom === 0 && dto.dateTo === 0) {
            evaluateError('Informe as datas corretamente')
            return false
        }
        if (!dto.dateReference) {
            evaluateError('Informe a data e hora a considerar')
            return false
        }
        return true
    }

    function evaluateError(error) {
        window.error(error)
    }

    onMount(() => {
        if (!rule.audienceRuleId) startEdit()
        ruleSelectedEvent = rule.event?.split(',') || []
        ruleSelectedValue = rule.value?.split(',') || []
        if (rule.dateType === AudienceRuleDateType.Fixed) {
            fixedDateFrom = rule.dateFrom
                ? dayjs(rule.dateFrom * 1000)
                      .utc()
                      .toDate()
                : new Date()
            fixedDateTo = rule.dateTo
                ? dayjs(rule.dateTo * 1000)
                      .utc()
                      .toDate()
                : new Date()
        }
        if (rule.dateType === AudienceRuleDateType.Relative) {
            let { factor } = getDynamicDateFactor(rule.dateFrom * 1000, rule.dateTo * 1000)
            dynamicDateFactor = factor
            dynamicDateFrom = (rule.dateFrom * 1000) / +dynamicDateFactor
            dynamicDateTo = (rule.dateTo * 1000) / +dynamicDateFactor
        }
        // se não exibe as condicionais, então a regra obrigatoriamente deve ser 1 (OR)
        if (firstRule) rule.act = 1
        // públicos estáticos possuem apenas regras de exclusão
        if (type === 'static') rule.act = 2
    })

    let parentRuleLabel
    $: {
        if (rule?.act === AudienceRuleAct.Exclude) {
            bgRed = true
            parentRuleLabel = 'e remover do público estas (NOT)'
        }
        if (rule?.act === AudienceRuleAct.IncludeAnd) {
            bgRed = false
            parentRuleLabel = 'mas apenas adicionar se forem uma destas (AND)'
        }
        if (rule?.act === AudienceRuleAct.IncludeOr) {
            bgRed = false
            parentRuleLabel = 'também adicionar estas (OR)'
        }
        lineRed = bgRed
        if (firstRule) {
            if (type === 'static') {
                parentRuleLabel = 'Excluir deste público'
            } else {
                parentRuleLabel = 'Incluir neste público'
            }
        }
    }

    // opções dos campos — o texto é o mesmo que estava nos <option>, só saiu do markup
    const actOptions = [
        { value: null, label: 'Escolha…' },
        { value: 0, label: 'mas apenas adicionar se forem uma destas (AND)' },
        { value: 1, label: 'também adicionar estas (OR)' },
        { value: 2, label: 'e remover do público estas (NOT)' },
    ]
    const dateTypeOptions = [
        { value: AudienceRuleDateType.Fixed, label: 'Fixa (data inicial e final)' },
        { value: AudienceRuleDateType.Relative, label: 'Relativa ao dia atual' },
    ]
    const dateReferenceOptions = [
        { value: 1, label: 'Hora do evento' },
        { value: 2, label: 'Hora da ação que gerou o evento' },
    ]
    const lastEventOptions = [
        { value: true, label: 'Apenas último evento' },
        { value: false, label: 'Todos os eventos' },
    ]
    // a lista de eventos vem do servidor quando a edição abre (loadEventTypes)
    $: eventOptions = eventTypes.map((eventType) => ({ value: eventType, label: eventType }))

    const factors = [3600000, 86400000, 2592000000, 31536000000]
    const factorsLabel = ['hora(s)', 'dia(s)', 'mes(es)', 'ano(s)']
    const factor_hour = 0
    const factor_day = 1
    const factor_month = 2
    const factor_year = 3
    // "De" e "Até" vão de 0 a 45 (45 dias cobre um mês e meio sem precisar trocar para meses)
    const maxRepeat = 46
    const repeatOptions = Array.from({ length: maxRepeat }, (_, i) => ({ value: i, label: String(i) }))
    const factorOptions = [
        { value: factors[factor_hour] * -1, label: 'horas atrás' },
        { value: factors[factor_day] * -1, label: 'dias atrás' },
        { value: factors[factor_month] * -1, label: 'meses atrás' },
        { value: factors[factor_year] * -1, label: 'anos atrás' },
        { value: factors[factor_hour], label: 'horas à frente' },
        { value: factors[factor_day], label: 'dias à frente' },
        { value: factors[factor_month], label: 'meses à frente' },
        { value: factors[factor_year], label: 'anos à frente' },
    ]
    function getDynamicDateFactor(dateFrom: number, dateTo: number) {
        let dateRef = Math.abs(dateFrom) > Math.abs(dateTo) ? dateFrom : dateTo
        const past = dateRef < 0 ? -1 : 1
        if (dateRef < 0) dateRef = dateRef * -1
        // menor unidade que representa as duas pontas sem sobra e cabe nas opções. Sem a exigência de
        // conta exata, subir o limite fazia 3 anos abrir como 36,5 meses (o mês aqui é de 30 dias)
        let pos = factors.findIndex((f) => dateFrom % f === 0 && dateTo % f === 0 && dateRef / f < maxRepeat)
        let factor = factors[pos]
        // hora, dia, mes, ano — nenhuma unidade exata: a menor em que o valor cabe
        if (pos < 0) {
            for (let index = 0; index < factors.length; index++) {
                const base = dateRef / maxRepeat
                pos = index
                factor = factors[index]
                if (base < factors[index]) break
            }
        }
        factor *= past
        let label = factorsLabel[pos]
        return { factor, label }
    }

    function friendlyDateType(dateType: AudienceRuleDateType, dateFrom: number, dateTo: number) {
        if (dateType === AudienceRuleDateType.Fixed) {
            return (
                dayjs(dateFrom * 1000)
                    .utc()
                    .format('DD/MM/YYYY') +
                ' até ' +
                dayjs(dateTo * 1000)
                    .utc()
                    .format('DD/MM/YYYY')
            )
        }

        let { factor, label } = getDynamicDateFactor(dateFrom * 1000, dateTo * 1000)
        dateFrom = Math.trunc(Math.trunc((dateFrom * 1000) / Math.abs(factor)))
        dateTo = Math.trunc(Math.trunc(dateTo * 1000) / Math.abs(factor))
        return 'Entre ' + dateFrom + ' e ' + dateTo + ' ' + label + ' ' + (factor < 0 ? 'atrás' : 'à frente')
    }
</script>

<div class="root audience-rule">
    {#if !edit}
        <div class="line-conector" class:lineRed><div class:bgRed>{parentRuleLabel}</div></div>
        <div class="regra">
            <div class="pessoas" class:bgRed>
                <div class="total">{rule.total === undefined ? '?' : rule.total}</div>
                <div class="label">pessoas</div>
            </div>
            <div class="eventos">
                {#each ruleSelectedEvent as item}
                    <div class="item">{item}</div>
                {/each}
            </div>
            <div class="periodo">
                {#if rule.dateType === AudienceRuleDateType.Fixed}
                    <div class="tipo">Data (fixa)</div>
                {:else}
                    <div class="tipo">Período (relativo a hoje)</div>
                {/if}
                <div class="data">{friendlyDateType(rule.dateType, rule.dateFrom, rule.dateTo)}</div>
            </div>
            <div class="act">
                <Zbutton color="primary" icon={faPenNib} title="Editar regra" on:click={startEdit} />
            </div>
        </div>
    {/if}
    {#if edit}
        <div class="edit-bg" />

        <div class="line-conector z-index-2" class:lineRed>
            {#if !firstRule && type !== 'static'}
                <div class="act-field">
                    <Zinput label="Como esta regra entra no público" type="select" options={actOptions} bind:value={rule.act} />
                </div>
            {:else}
                <div class:bgRed>{parentRuleLabel}</div>
            {/if}
        </div>
        <div class="edit">
            <div class="edit-green" class:bgRed>
                {#if rule.act !== AudienceRuleAct.Exclude}
                    Inclusão de
                {:else}
                    Exclusão de
                {/if}
                <div class="preview">{rule.total ?? '?'}</div>
                <div>pessoas</div>
                <Zbutton class="mt-2" skin="outline" label="Atualizar" on:click={updatePreview} />
            </div>
            <div class="edit-content">
                <div class="edit-fields">
                    <div class="block">
                        <ZContainer gapRow="8px" gapCol="12px">
                            <ZContainerCol size={12}>
                                <Zinput label="Eventos a serem procurados" type="multiselect" options={eventOptions} bind:value={ruleSelectedEvent} />
                                <div class="dica">Escolha um ou mais. Clique na etiqueta para tirar da lista.</div>
                            </ZContainerCol>
                            <ZContainerCol size={12}>
                                <div>
                                    <Zinput label="Considerar apenas eventos com este conteúdo" type="textarea" rows={3} bind:value={rule.value} />
                                    <div class="dica">Valores separados por vírgula. Ou deixe em branco para não filtrar.</div>
                                    <!-- regras avaliadas em events-api: use-cases/event-rule-evaluation.service.ts -->
                                    <details class="value-help">
                                        <summary>Como usar os filtros de conteúdo</summary>
                                        <ul class="value-help-list">
                                            <li>
                                                <code>corte</code> — <b>contém</b>: encontra eventos que tenham esse texto em qualquer parte.
                                                <div class="example">Ex.: <code>corte</code> encontra "Corte de cabelo" e também "Recorte".</div>
                                            </li>
                                            <li>
                                                <code>corte, barba</code> — a vírgula é <b>OU</b>: basta o evento ter um dos valores.
                                                <div class="example">Ex.: encontra eventos com "corte" ou com "barba".</div>
                                            </li>
                                            <li>
                                                <code>[corte]</code> — colchetes são <b>exato</b>: o conteúdo precisa ser igual ao valor, e não apenas conter o
                                                texto. Quando o evento tem vários itens, basta um deles ser igual.
                                                <div class="example">Ex.: <code>[corte]</code> encontra "Corte", mas não "Corte de cabelo".</div>
                                            </li>
                                            <li>
                                                <code>(corte, barba)</code> — parênteses são <b>E</b>: o evento precisa ter todos os valores ao mesmo tempo.
                                                <div class="example">Ex.: encontra "Corte e barba", mas não um evento só com "Corte".</div>
                                            </li>
                                            <li>
                                                As regras podem ser combinadas: <code>(corte, barba), [escova]</code> encontra eventos com "corte" e "barba", ou
                                                exatamente "escova".
                                            </li>
                                            <li>
                                                Maiúsculas e acentos são ignorados: <code>promocao</code> encontra "Promoção". Não use vírgula dentro dos colchetes.
                                            </li>
                                        </ul>
                                    </details>
                                </div>
                            </ZContainerCol>
                        </ZContainer>
                    </div>
                    <div class="block">
                        <ZContainer gapRow="8px" gapCol="8px">
                            <Zinput size={4} sizeM={12} label="A data do evento deve ser" type="select" options={dateTypeOptions} bind:value={rule.dateType} />
                            {#if rule.dateType === AudienceRuleDateType.Fixed}
                                <Zinput size={4} sizeM={6} label="De" type="date" bind:value={fixedDateFrom} />
                                <Zinput size={4} sizeM={6} label="Até" type="date" bind:value={fixedDateTo} />
                            {/if}
                            {#if rule.dateType === AudienceRuleDateType.Relative}
                                <Zinput size={2} sizeM={6} label="De" type="select" options={repeatOptions} bind:value={dynamicDateFrom} />
                                <Zinput size={2} sizeM={6} label="Até" type="select" options={repeatOptions} bind:value={dynamicDateTo} />
                                <Zinput size={4} sizeM={12} label="Contados em" type="select" options={factorOptions} bind:value={dynamicDateFactor} />
                            {/if}
                        </ZContainer>
                    </div>
                    <div class="block">
                        <ZContainer gapRow="8px" gapCol="8px">
                            <Zinput
                                size={6}
                                sizeM={12}
                                label="A data e hora a considerar deve ser"
                                type="select"
                                options={dateReferenceOptions}
                                bind:value={rule.dateReference}
                            />
                            <Zinput size={6} sizeM={12} label="Eventos a considerar" type="select" options={lastEventOptions} bind:value={rule.lastEvent} />
                        </ZContainer>
                    </div>
                </div>
                <div class="edit-act">
                    <div class="block">
                        <div class="act">
                            <Zbutton color="red" icon={faTrashAlt} label="Excluir regra" on:click={removeRule} />
                            <div class="spacer" />
                            <Zbutton color="secondary" icon={faTimes} label="Cancelar edição" on:click={cancelEdit} />
                            <Zbutton color="primary" icon={faSave} label="Salvar regra" on:click={saveRule} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>
<LoadSpinner {show} />

<style>
    /* a regra ocupa a largura da área de regras. Com "margin: 0 auto" (de quando havia max-width)
       a margem automática cancelava o alinhamento esticado do flex, e aí a largura do painel passava
       a ser a do conteúdo: escolher eventos ia empurrando a tela. */
    .root {
        width: 100%;
        min-width: 0;
        margin-left: auto;
        margin-right: auto;
        max-width: 880px;
    }
    .edit-bg {
        z-index: 1;
        position: absolute;
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 0px;
        width: 100%;
        height: 100%;
        background: #00000080;
    }
    /* o conector abre um select; ele vem antes do bloco de edição no DOM, então precisa de um
       z-index maior para a lista não sumir atrás do bloco (com o mesmo valor, ganha quem vem depois) */
    .z-index-2 {
        z-index: 3;
        position: relative;
    }
    /* sem overflow: hidden — ele recortava a lista de um campo que abrisse para cima. O canto
       arredondado da coluna verde é dela mesma (ver .edit-green). */
    .edit {
        z-index: 2;
        display: flex;
        position: relative;
        border-radius: 8px;
        background: #333333;
    }
    .edit-green {
        background-color: #379e75;
        border-radius: 8px 0px 0px 8px;
        flex: 0 0 110px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    .edit-content {
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        min-width: 0;
    }
    .edit-fields {
        flex-grow: 1;
    }
    .edit-act {
        background: #99999922;
    }
    .block {
        position: relative;
        padding: 12px;
    }

    /* a linha de instrução que acompanha um campo (era .text-muted do Bootstrap) */
    .dica {
        margin-top: 6px;
        font-size: 12px;
        opacity: 0.7;
        line-height: 1.35;
    }

    /* o campo que escolhe como a regra entra no público fica solto na linha conectora */
    .act-field {
        width: 460px;
        max-width: 100%;
        margin: 10px 0px 10px 8px;
    }
    .value-help {
        margin-top: 8px;
        font-size: 0.85rem;
    }
    .value-help summary {
        cursor: pointer;
        color: #868e96;
        user-select: none;
    }
    .value-help summary:hover {
        color: inherit;
    }
    .value-help-list {
        margin: 8px 0 0;
        padding-left: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .value-help-list .example {
        opacity: 0.7;
    }
    .block .act {
        display: flex;
        align-items: center;
        gap: 8px;
        /* o menu flutuante do app mora no canto inferior direito: sem esta folga ele cobre o Salvar */
        padding-right: 76px;
    }
    .block .act .spacer {
        flex: 1;
    }

    .regra {
        display: flex;
        background: #99999922;
        border-radius: 8px;
        overflow: hidden;
    }
    .regra > div {
        padding: 4px 8px;
    }
    .regra .pessoas {
        background-color: #379e75;
        flex: 0 0 110px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        line-height: 1.15;
    }
    .regra .pessoas .total {
        font-size: 24px;
    }
    .regra .eventos {
        flex: 1 1 auto;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }
    .regra .eventos .item {
        padding: 2px 4px;
        margin: 2px 4px;
        border: 1px solid #99999915;
        border-radius: 4px;
    }
    .regra .periodo {
        flex: 0 0 240px;
        border-left: 1px solid #99999922;
        border-right: 1px solid #99999922;
        display: flex;
        justify-content: center;
        flex-direction: column;
        opacity: 0.8;
    }
    .regra .act {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    /* o conector é um bloco de largura cheia com só a pílula (ou o campo) desenhada nele. Sem isto
       ele captura o clique em toda a faixa, inclusive sobre a lista de um campo que abra para cima. */
    .line-conector {
        border-left: 2px solid #328866;
        margin-left: 24px;
        pointer-events: none;
    }
    .line-conector > * {
        pointer-events: auto;
    }
    .line-conector > div:not(.act-field) {
        margin: 10px 0px;
        color: #ddd;
        display: inline-block;
        font-size: 13px;
        padding: 2px 6px 2px 2px;
        border-radius: 0px 4px 4px 0px;
        background-color: #379e75;
    }

    .bgRed {
        background-color: rgb(214, 79, 122) !important;
    }
    .lineRed {
        border-left: 2px solid rgb(214, 79, 122) !important;
    }
</style>
